import { createSlice } from '@reduxjs/toolkit';
import axiosInstance from 'src/utils/axiosInstance';

const initialState = {
  corporations: [],
  totalCount: 0,
  totalPages: 0,
  loading: false,
  error: null,
};

export const corporationSlice = createSlice({
  name: 'corporation',
  initialState,
  reducers: {
    setCorporations: (state, action) => {
      state.corporations = action.payload;
    },
    setTotalCount: (state, action) => {
      state.totalCount = action.payload;
    },
    setTotalPages: (state, action) => {
      state.totalPages = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
});

export const { setCorporations, setTotalCount, setTotalPages, setLoading, setError } = corporationSlice.actions;

// Get corporations (paginated & searchable)
export const getCorporations = (filters) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const page = filters.page || 1;
    const limit = filters.limit || 10;
    const search = filters.search || '';
    const response = await axiosInstance.get('/admin/corporation/list', {
      params: { page, limit, search }
    });
    if (response.data && response.data.status === true) {
      const result = response.data.data.result || [];
      const totalCount = response.data.data.totalCount || result.length;
      const totalPages = response.data.data.totalPages || 1;
      dispatch(setCorporations(result));
      dispatch(setTotalCount(totalCount));
      dispatch(setTotalPages(totalPages));
      return { status: true, data: result, totalCount, totalPages };
    } else {
      dispatch(setCorporations([]));
      dispatch(setTotalCount(0));
      dispatch(setTotalPages(0));
      const errorMsg = response.data?.message || 'Failed to fetch corporations';
      dispatch(setError(errorMsg));
      return { status: false, message: errorMsg };
    }
  } catch (error) {
    dispatch(setCorporations([]));
    dispatch(setTotalCount(0));
    dispatch(setTotalPages(0));
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch corporations';
    dispatch(setError(errorMsg));
    return { status: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Get single corporation details (prefill edit form)
export const getCorporationDetails = (id) => async () => {
  try {
    const response = await axiosInstance.get(`/admin/corporation/getbyId/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data };
    } else {
      return { status: false, message: response.data?.message || 'Failed to fetch corporation details' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch corporation details';
    return { status: false, message: errorMsg };
  }
};

// Add new corporation
export const addCorporation = (payload) => async () => {
  try {
    const response = await axiosInstance.post('/admin/corporation/add', payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to add corporation' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to add corporation';
    return { status: false, message: errorMsg };
  }
};

// Update corporation details
export const updateCorporation = (id, payload) => async () => {
  try {
    const response = await axiosInstance.put(`/admin/corporation/update/${id}`, payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to update corporation' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to update corporation';
    return { status: false, message: errorMsg };
  }
};

// Delete corporation
export const deleteCorporation = (id) => async () => {
  try {
    const response = await axiosInstance.delete(`/admin/corporation/delete/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to delete corporation' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to delete corporation';
    return { status: false, message: errorMsg };
  }
};

export default corporationSlice.reducer;
